import {
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Select,
  Typography,
} from "@mui/material";
import * as React from "react";
import { Link } from "react-router";

import { MirrorHighlightTextarea } from "@/shared/components";
import { SkillSummary, SkillVersionRecord } from "@/shared/domain/skills";
import { useSkillVersions } from "@/shared/domain/skills/hooks";
import {
  ResolvedSlug,
  activeSlugAtCursor,
  parseSlugs,
  resolveSlugs,
} from "@/shared/domain/skills/slugParse";
import { useCaretAutocomplete, useComboboxIds } from "@/shared/hooks";
import { Goto } from "@/shared/navigation/Goto";

import styles from "./SkillSlugComposer.module.css";

/** Maximum suggestions shown in the autocomplete menu. */
const MAX_SUGGESTIONS = 8;

interface Props {
  value: string;
  onChange: (next: string) => void;
  /** Called on Enter (without shift). The composer does not own send eligibility. */
  onSubmit: () => void;
  placeholder?: string;
  disabled?: boolean;
  /** Personal-tab skills used to drive autocomplete suggestions + slug resolution. */
  skills: SkillSummary[];
  textareaRef?: React.MutableRefObject<HTMLTextAreaElement | null>;
}

/** Imperative API the parent (chat input toolbar) uses to drive autocomplete. */
export interface SkillSlugComposerHandle {
  /**
   * Reconcile autocomplete state against the textarea's current value + caret.
   * Call after programmatically inserting a slash (e.g., from the toolbar
   * skill button) so the popper opens even though no input event fired.
   */
  openAutocompleteAtCaret: () => void;
}

/**
 * Chat composer that supports inline ``/skill-name[@vN]`` slug tokens.
 *
 * Visible UI is a normal-looking textarea; what the user sees is actually a
 * mirror ``<div>`` rendered behind a transparent-text textarea, with detected
 * slugs wrapped in styled spans. Caret and selection come from the textarea;
 * coloring + click affordances come from the overlay. Autocomplete fires on
 * `/` and on subsequent slug-character keystrokes.
 *
 * Send eligibility is the parent's responsibility — this component only
 * surfaces ``onSubmit`` on Enter.
 */
export const SkillSlugComposer = React.forwardRef<
  SkillSlugComposerHandle,
  Props
>(function SkillSlugComposer(
  { value, onChange, onSubmit, placeholder, disabled, skills, textareaRef },
  forwardedRef,
) {
  const internalRef = React.useRef<HTMLTextAreaElement | null>(null);
  // MirrorHighlightTextarea attaches internalRef; mirror the node onto the
  // optional forwarded prop so the parent toolbar can still focus / insert.
  React.useEffect(() => {
    if (textareaRef) {
      textareaRef.current = internalRef.current;
    }
  });
  const overlayRef = React.useRef<HTMLDivElement | null>(null);
  const paperRef = React.useRef<HTMLDivElement | null>(null);
  const [versionPopover, setVersionPopover] = React.useState<{
    anchorEl: HTMLElement;
    slug: ResolvedSlug;
  } | null>(null);
  // Per-suggestion-row version pick. Keys are skill_id; value `null` means
  // "Latest" and inserts `/name` (no @vN). The Latest case is the common path
  // for typed slug auto-acceptance — the version selector is opt-in.
  const [chosenVersions, setChosenVersions] = React.useState<
    Record<string, number | null>
  >({});

  // Shared caret-autocomplete engine. This composer supplies the slug detector
  // and its own suggestion + overlay rendering. The per-row version Select
  // portals its listbox to <body>, so keepOpenSelector carves it (and any MUI
  // menu) out of the outside-click dismissal.
  const {
    autocomplete,
    setHighlight,
    clampHighlight,
    refresh,
    close,
    anchorEl,
    syncOverlayScroll,
    handleNavKeyDown,
    accept,
  } = useCaretAutocomplete({
    onChange,
    textareaRef: internalRef,
    overlayRef,
    paperRef,
    detectToken: activeSlugAtCursor,
    keepOpenSelector: '[role="listbox"], [role="menu"]',
  });

  const { listboxId, optionId } = useComboboxIds();

  React.useImperativeHandle(
    forwardedRef,
    () => ({
      openAutocompleteAtCaret: () => {
        const ta = internalRef.current;
        if (ta === null) {
          return;
        }
        refresh(ta.value, ta.selectionEnd ?? ta.value.length);
      },
    }),
    [refresh],
  );

  // Resolve all slugs in the current text. Memoized so the overlay re-render is
  // cheap on every keystroke.
  const resolvedSlugs: ResolvedSlug[] = React.useMemo(
    () => resolveSlugs(parseSlugs(value), skills),
    [value, skills],
  );

  const filteredSuggestions = React.useMemo(() => {
    if (autocomplete === null) {
      return [];
    }
    const q = autocomplete.query.toLowerCase();
    return skills
      .filter((s) => s.latest_version !== null)
      .filter((s) => s.skill.name.toLowerCase().includes(q))
      .slice(0, MAX_SUGGESTIONS);
  }, [skills, autocomplete]);

  // Keep the highlight valid as the filtered list shrinks.
  React.useEffect(() => {
    clampHighlight(filteredSuggestions.length);
  }, [clampHighlight, filteredSuggestions.length]);

  function handleChange(event: React.ChangeEvent<HTMLTextAreaElement>) {
    onChange(event.target.value);
    refresh(
      event.target.value,
      event.target.selectionEnd ?? event.target.value.length,
    );
  }

  function handleSelect(event: React.SyntheticEvent<HTMLTextAreaElement>) {
    // Caret moved without text change — refresh the autocomplete state.
    const ta = event.currentTarget;
    refresh(ta.value, ta.selectionEnd ?? ta.value.length);
  }

  function acceptSuggestion(
    skillSummary: SkillSummary,
    versionOverride: number | null = chosenVersions[
      skillSummary.skill.skill_id
    ] ?? null,
  ) {
    // Two visible skills can share a name (private vs. org). When the user
    // explicitly picks one of those, encode the accessibility into the slug
    // so the send-time resolver honors their pick instead of falling back to
    // private-wins. Bare slugs stay uncluttered when there's no collision.
    const hasNameCollision = skills.some(
      (other) =>
        other.skill.skill_id !== skillSummary.skill.skill_id &&
        other.skill.name === skillSummary.skill.name,
    );
    const accessSuffix = hasNameCollision
      ? `(${skillSummary.skill.accessibility})`
      : "";
    const versionSuffix =
      versionOverride === null ? "" : `@v${versionOverride}`;
    const slug = `/${skillSummary.skill.name}${accessSuffix}${versionSuffix}`;
    // The engine splices the slug over the active token and restores the caret.
    accept(slug);
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLTextAreaElement>) {
    const consumed = handleNavKeyDown(
      event,
      filteredSuggestions.length,
      (index) => acceptSuggestion(filteredSuggestions[index]),
    );
    if (consumed) {
      return;
    }
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      onSubmit();
    }
  }

  // The overlay rebuilds a list of {text, slug?} segments out of the source
  // string so React can render colored spans for the slug ranges. Walking the
  // resolved-slug list once is O(value.length) and keeps overlay text in
  // perfect lockstep with the textarea text (modulo CSS font matching).
  const overlaySegments = React.useMemo(() => {
    const segments: Array<{
      text: string;
      slug?: ResolvedSlug;
    }> = [];
    let cursor = 0;
    for (const slug of resolvedSlugs) {
      if (slug.start > cursor) {
        segments.push({ text: value.slice(cursor, slug.start) });
      }
      segments.push({ text: value.slice(slug.start, slug.end), slug });
      cursor = slug.end;
    }
    if (cursor < value.length) {
      segments.push({ text: value.slice(cursor) });
    }
    return segments;
  }, [value, resolvedSlugs]);

  function handleSlugClick(
    event: React.MouseEvent<HTMLElement>,
    slug: ResolvedSlug,
  ) {
    if (slug.skill === null) {
      return;
    }
    event.stopPropagation();
    setVersionPopover({ anchorEl: event.currentTarget, slug });
  }

  function applyVersionChange(slug: ResolvedSlug, version: number | null) {
    const skillName = slug.skill?.name ?? slug.name;
    // Preserve any existing accessibility disambiguator on the original slug
    // so re-pinning a version doesn't silently flip private↔org on a
    // collision name.
    const accessSuffix =
      slug.accessibility === null ? "" : `(${slug.accessibility})`;
    const versionSuffix = version === null ? "" : `@v${version}`;
    const replacement = `/${skillName}${accessSuffix}${versionSuffix}`;
    const next =
      value.slice(0, slug.start) + replacement + value.slice(slug.end);
    onChange(next);
    setVersionPopover(null);
  }

  const menuHasOptions =
    autocomplete !== null && filteredSuggestions.length > 0;
  const activeOptionId =
    autocomplete !== null && filteredSuggestions.length > 0
      ? optionId(autocomplete.highlight)
      : undefined;

  return (
    <div className={styles.composerRoot}>
      <MirrorHighlightTextarea
        value={value}
        overlay={overlaySegments.map((segment, idx) => {
          if (segment.slug === undefined) {
            return <span key={idx}>{segment.text}</span>;
          }
          const isResolved = segment.slug.skill !== null;
          return (
            <span
              key={idx}
              className={
                isResolved ? styles.slugResolved : styles.slugUnresolved
              }
              onClick={(e) => handleSlugClick(e, segment.slug!)}
              role={isResolved ? "button" : undefined}
              tabIndex={isResolved ? 0 : undefined}
            >
              {segment.text}
            </span>
          );
        })}
        textareaRef={internalRef}
        overlayRef={overlayRef}
        onChange={handleChange}
        onSelect={handleSelect}
        onScroll={syncOverlayScroll}
        onKeyDown={handleKeyDown}
        expanded={menuHasOptions}
        listboxId={listboxId}
        activeOptionId={activeOptionId}
        ariaLabel="Chat message input"
        placeholder={placeholder}
        disabled={disabled}
        rows={3}
        textClassName={styles.composerText}
        textareaClassName={styles.composerTextarea}
        overlayClassName={styles.slugOverlay}
      />
      <Popper
        open={autocomplete !== null && anchorEl !== null}
        anchorEl={anchorEl}
        placement="bottom-start"
        modifiers={[{ name: "offset", options: { offset: [0, 4] } }]}
        className={styles.popper}
      >
        <Paper ref={paperRef} elevation={4} className={styles.suggestionsPaper}>
          {filteredSuggestions.length === 0 ? (
            <div className={styles.suggestionsEmpty}>
              <Typography
                variant="body2"
                className={styles.suggestionsEmptyText}
              >
                {skills.length === 0
                  ? "No skills in your Personal tab. Subscribe to a skill in skill settings to use slash commands."
                  : `No skills match “${autocomplete?.query ?? ""}”.`}
              </Typography>
            </div>
          ) : (
            <MenuList dense id={listboxId} role="listbox">
              {filteredSuggestions.map((summary, idx) => {
                const chosen = chosenVersions[summary.skill.skill_id] ?? null;
                return (
                  <MenuItem
                    key={summary.skill.skill_id}
                    id={optionId(idx)}
                    role="option"
                    aria-selected={idx === autocomplete?.highlight}
                    selected={idx === autocomplete?.highlight}
                    onClick={() => acceptSuggestion(summary)}
                    onMouseEnter={() => setHighlight(idx)}
                  >
                    <div className={styles.suggestionRow}>
                      <div className={styles.suggestionName}>
                        {summary.skill.name}
                      </div>
                      <div className={styles.suggestionDescription}>
                        {summary.latest_version?.description ?? ""}
                      </div>
                    </div>
                    <div
                      className={styles.versionPickerWrapper}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <SuggestionVersionPicker
                        skillId={summary.skill.skill_id}
                        latestVersion={summary.latest_version?.version ?? null}
                        chosen={chosen}
                        onPick={(version) =>
                          setChosenVersions((prev) => ({
                            ...prev,
                            [summary.skill.skill_id]: version,
                          }))
                        }
                      />
                    </div>
                  </MenuItem>
                );
              })}
            </MenuList>
          )}
          <div className={styles.suggestionsFooter}>
            {filteredSuggestions.length > 0 && (
              <Typography variant="caption" className={styles.suggestionsHint}>
                ↑↓ to navigate · Enter or Tab to insert · Esc to dismiss
              </Typography>
            )}
            <Link
              to={Goto.route.AgentSkills}
              className={styles.suggestionsManageLink}
              onClick={close}
            >
              Manage skills →
            </Link>
          </div>
        </Paper>
      </Popper>
      {versionPopover !== null && (
        <VersionPopover
          anchorEl={versionPopover.anchorEl}
          slug={versionPopover.slug}
          onClose={() => setVersionPopover(null)}
          onPick={(version) => applyVersionChange(versionPopover.slug, version)}
        />
      )}
    </div>
  );
});

interface VersionPopoverProps {
  anchorEl: HTMLElement;
  slug: ResolvedSlug;
  onClose: () => void;
  onPick: (version: number | null) => void;
}

/**
 * Tiny version-picker popover anchored to a highlighted slug span.
 *
 * Versions are loaded lazily on first open via :func:`useSkillVersions`. The
 * "Latest" option clears the explicit ``@vN`` pin so the slug falls back to
 * MAX(version) at resolution time.
 */
function VersionPopover({
  anchorEl,
  slug,
  onClose,
  onPick,
}: VersionPopoverProps) {
  const skillId = slug.skill?.skill_id;
  const versionsQuery = useSkillVersions(skillId);
  const versions: SkillVersionRecord[] = versionsQuery.data ?? [];

  return (
    <Popper
      open
      anchorEl={anchorEl}
      placement="bottom-start"
      modifiers={[{ name: "offset", options: { offset: [0, 4] } }]}
      onMouseLeave={onClose}
    >
      <Paper elevation={4} className={styles.versionPopover}>
        <Typography variant="caption" className={styles.versionPopoverHeader}>
          {slug.skill?.name ?? slug.name}
        </Typography>
        <Select
          size="small"
          value={slug.version ?? "latest"}
          onChange={(e) => {
            const raw = e.target.value;
            onPick(raw === "latest" ? null : Number(raw));
          }}
          fullWidth
        >
          <MenuItem value="latest">Latest</MenuItem>
          {versions.map((v) => (
            <MenuItem key={v.version} value={v.version}>
              v{v.version}
            </MenuItem>
          ))}
        </Select>
      </Paper>
    </Popper>
  );
}

interface SuggestionVersionPickerProps {
  skillId: string;
  latestVersion: number | null;
  chosen: number | null;
  onPick: (version: number | null) => void;
}

/**
 * Inline version selector rendered to the right of each autocomplete row.
 *
 * Default value is "Latest" (which inserts `/skill-name` with no `@vN`
 * suffix); the user can switch to a specific pinned version before clicking
 * the row. The wrapping div in the parent MenuItem already stops click
 * propagation, so opening this picker doesn't double-fire as a row-accept.
 * The full version list is lazy-loaded only when the picker is actually
 * opened — one fetch per skill at most, and only if the user cares about
 * non-latest.
 */
function SuggestionVersionPicker({
  skillId,
  latestVersion,
  chosen,
  onPick,
}: SuggestionVersionPickerProps) {
  const [didOpen, setDidOpen] = React.useState(false);
  const versionsQuery = useSkillVersions(didOpen ? skillId : undefined);
  const versions = (versionsQuery.data ?? [])
    .map((v) => v.version)
    .filter((v) => v !== latestVersion);

  return (
    <Select
      size="small"
      variant="standard"
      disableUnderline
      value={chosen ?? "latest"}
      onOpen={() => setDidOpen(true)}
      onChange={(e) => {
        const raw = e.target.value;
        onPick(raw === "latest" ? null : Number(raw));
      }}
      // Sit above the suggestions paper, which is pinned to 1310 via
      // `.popper`. Without this the version dropdown renders behind the
      // paper and the hint bar (default Menu z-index is 1300).
      MenuProps={{ style: { zIndex: 1320 } }}
      className={styles.versionSelect}
    >
      <MenuItem value="latest">
        {latestVersion === null ? "—" : `v${latestVersion}`}
      </MenuItem>
      {versions.map((v) => (
        <MenuItem key={v} value={v}>
          v{v}
        </MenuItem>
      ))}
    </Select>
  );
}
